import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Link as MUILink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { History, Home, Undo } from '@mui/icons-material';
import dayjs, { type Dayjs } from 'dayjs';
import { FilterBar } from '@/components/transactions/FilterBar';
import type { TransactionDetailSection } from '@/components/transactions/TransactionDetailModal';
import { TransactionDetailModal } from '@/components/transactions/TransactionDetailModal';
import { TransactionLogCard } from '@/components/transactions/TransactionLogCard';
import type { TransactionType } from '@/components/transactions/mockTransactionLog';
import { usePageTitle } from '@/hooks/usePageTitle';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { useTransactionLog } from '@/hooks/useTransactionLog';
import { getErrorMessage } from '@/utils/httpClient';
import { multiWordMatch } from '@/utils/multiWordMatch';

const TRANSACTION_TYPE_VALUES: readonly TransactionType[] = [
  'ISSUE_OUT',
  'TURN_IN',
  'EXCHANGE',
  'THR_DUE_DATE_EXTENDED',
  'THR_TO_PHR_CONVERTED',
  'DISPOSAL',
  'TRANSFER',
  'SHIPMENT',
  'MAINTENANCE_SEND',
  'MAINTENANCE_RECEIVE',
  'DD200',
  'DA7923',
  'MFR',
  'DA4949',
  'PURCHASE_REQUISITION_CREATE',
  'PURCHASE_REQUISITION_RECEIVE',
  'PURCHASE_REQUISITION_CANCEL',
  'IAR_ADJUSTMENT',
  'CORRECTION_FULL_DOCUMENT',
  'CORRECTION_ITEM_LEVEL',
  'NON_RECOVERABLE_CLEAR',
  'INDIVIDUAL_CLEARED',
  'INDIVIDUAL_REACTIVATED',
] as const;

const isTransactionType = (value: string): value is TransactionType =>
  (TRANSACTION_TYPE_VALUES as readonly string[]).includes(value);

export const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  usePageTitle('Transaction Log');
  const person = usePersonLabels('front_window');

  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [initialDetailSection, setInitialDetailSection] = useState<
    TransactionDetailSection | undefined
  >(undefined);

  // Deep-link: open a transaction modal when ?txnId= is present in the URL
  const txnIdParam = searchParams.get('txnId');
  useEffect(() => {
    if (txnIdParam) {
      setSelectedTransactionId(txnIdParam);
      // Remove the param so a page refresh doesn't re-open the stale modal
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('txnId');
        return next;
      }, { replace: true });
    }
  }, [txnIdParam, setSearchParams]);

  const searchText = searchParams.get('q') ?? '';
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const typesParam = searchParams.get('types');
  const handReceiptTypesParam = searchParams.get('handReceiptTypes');
  const memberId = searchParams.get('memberId')?.trim() ?? '';
  const memberName = searchParams.get('memberName')?.trim() ?? '';

  const dateFrom = useMemo<Dayjs | null>(() => {
    if (!fromParam) return dayjs().subtract(30, 'day');
    const parsed = dayjs(fromParam);
    return parsed.isValid() ? parsed : dayjs().subtract(30, 'day');
  }, [fromParam]);

  const dateTo = useMemo<Dayjs | null>(() => {
    if (!toParam) return dayjs();
    const parsed = dayjs(toParam);
    return parsed.isValid() ? parsed : dayjs();
  }, [toParam]);

  // Clamp URL-supplied date range to a maximum of 30 days.
  useEffect(() => {
    if (dateFrom && dateTo && dateTo.diff(dateFrom, 'day') > 30) {
      const clampedFrom = dateTo.subtract(30, 'day');
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('from', clampedFrom.format('YYYY-MM-DD'));
          return next;
        },
        { replace: true },
      );
    }
  }, [dateFrom, dateTo, setSearchParams]);

  const transactionTypes = useMemo<TransactionType[]>(() => {
    if (!typesParam) return [];
    return typesParam
      .split(',')
      .map((value) => value.trim())
      .filter((value): value is TransactionType => isTransactionType(value));
  }, [typesParam]);

  const handReceiptTypes = useMemo<Array<'PHR' | 'THR'>>(() => {
    if (!handReceiptTypesParam) return [];
    return handReceiptTypesParam
      .split(',')
      .map((value) => value.trim())
      .filter((value): value is 'PHR' | 'THR' => value === 'PHR' || value === 'THR');
  }, [handReceiptTypesParam]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(updates)) {
            const trimmed = typeof value === 'string' ? value.trim() : '';
            if (!trimmed) {
              next.delete(key);
            } else {
              next.set(key, trimmed);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const dateTimeFormatter = useMemo(
    () => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
    [],
  );

  const formatDateTime = useCallback(
    (value: string | null | undefined) => {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return dateTimeFormatter.format(date);
    },
    [dateTimeFormatter],
  );

  const fromForApi = dateFrom?.isValid() ? dateFrom.format('YYYY-MM-DD') : undefined;
  const toForApi = dateTo?.isValid() ? dateTo.format('YYYY-MM-DD') : undefined;

  const {
    data: serverFilteredTransactions = [],
    isLoading: isServerLoading,
    isFetching: isServerFetching,
    isError: isServerError,
    error: serverError,
    refetch: refetchServer,
  } = useTransactionLog({
    from: fromForApi,
    to: toForApi,
    types: transactionTypes,
    handReceiptTypes: handReceiptTypes,
    memberId: memberId || undefined,
  });

  const visibleTransactions = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return serverFilteredTransactions;
    }
    return serverFilteredTransactions.filter((transaction) => {
      const haystackParts = [
        transaction.documentNumber,
        transaction.searchableDocumentNumbers ?? '',
        transaction.transactionType,
        transaction.documentType,
        transaction.memberName,
        transaction.logFormNumber ?? '',
        transaction.sourceWorkflow ?? '',
        transaction.notes ?? '',
      ];
      const haystack = haystackParts.join(' ').toLowerCase();
      return multiWordMatch(haystack, query);
    });
  }, [searchText, serverFilteredTransactions]);

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">Transaction Log</Typography>
        </Breadcrumbs>

        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <History color="primary" sx={{ fontSize: 36 }} />
            <Typography variant="h4" component="h1">
              Transaction Log
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<Undo />}
              onClick={() => navigate('/correction')}
            >
              Document Correction
            </Button>
            <Chip
              size="small"
              label="READ-ONLY"
              variant="outlined"
              sx={{ fontWeight: 800, letterSpacing: '0.08em' }}
            />
          </Stack>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
          Read-only audit log of completed workflow transactions.
        </Typography>
        {memberId && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Chip
              color="primary"
              variant="outlined"
              label={`${person.label}: ${memberName || memberId}`}
              onDelete={() => updateSearchParams({ memberId: null, memberName: null })}
            />
          </Stack>
        )}
      </Box>

      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <FilterBar
            searchText={searchText}
            onSearchTextChange={(value) => updateSearchParams({ q: value })}
            dateFrom={dateFrom}
            onDateFromChange={(value) => {
              if (value?.isValid()) {
                const newFrom = value;
                const maxTo = newFrom.add(30, 'day');
                const adjustedTo = !dateTo || dateTo.isAfter(maxTo) || dateTo.isBefore(newFrom)
                  ? (maxTo.isAfter(dayjs()) ? dayjs() : maxTo)
                  : dateTo;
                updateSearchParams({
                  from: newFrom.format('YYYY-MM-DD'),
                  to: adjustedTo.format('YYYY-MM-DD'),
                });
              } else {
                updateSearchParams({ from: null });
              }
            }}
            dateTo={dateTo}
            onDateToChange={(value) => {
              if (value?.isValid()) {
                const newTo = value;
                const minFrom = newTo.subtract(30, 'day');
                const adjustedFrom = !dateFrom || dateFrom.isBefore(minFrom) || dateFrom.isAfter(newTo)
                  ? minFrom
                  : dateFrom;
                updateSearchParams({
                  to: newTo.format('YYYY-MM-DD'),
                  from: adjustedFrom.format('YYYY-MM-DD'),
                });
              } else {
                updateSearchParams({ to: null });
              }
            }}
            transactionTypes={transactionTypes}
            onTransactionTypesChange={(value) =>
              updateSearchParams({ types: value.length > 0 ? value.join(',') : null })
            }
            handReceiptTypes={handReceiptTypes}
            onHandReceiptTypesChange={(value) =>
              updateSearchParams({ handReceiptTypes: value.length > 0 ? value.join(',') : null })
            }
            onRefresh={() => {
              const hadServerFilters =
                Boolean(searchParams.get('from')) ||
                Boolean(searchParams.get('to')) ||
                Boolean(searchParams.get('types')) ||
                Boolean(searchParams.get('handReceiptTypes')) ||
                Boolean(searchParams.get('memberId'));

              const next = new URLSearchParams(searchParams);
              next.delete('q');
              next.delete('from');
              next.delete('to');
              next.delete('types');
              next.delete('handReceiptTypes');
              next.delete('memberId');
              next.delete('memberName');
              setSearchParams(next, { replace: true });
              if (!hadServerFilters) {
                void refetchServer();
              }
            }}
            isRefreshing={isServerFetching}
          />

          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {visibleTransactions.length} transaction
              {visibleTransactions.length === 1 ? '' : 's'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Read-only audit log
            </Typography>
          </Box>

          {isServerLoading ? (
            <Box
              sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading transactions…
              </Typography>
            </Box>
          ) : isServerError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => void refetchServer()}>
                  Retry
                </Button>
              }
            >
              {getErrorMessage(serverError)}
            </Alert>
          ) : visibleTransactions.length === 0 ? (
            <Box sx={{ py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No transactions match your filters.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {visibleTransactions.map((transaction) => (
                <TransactionLogCard
                  key={transaction.id}
                  transaction={transaction}
                  formatDateTime={formatDateTime}
                  onOpen={(transactionId, options) => {
                    setSelectedTransactionId(transactionId);
                    setInitialDetailSection(options?.initialSection);
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      <TransactionDetailModal
        open={Boolean(selectedTransactionId)}
        transactionId={selectedTransactionId}
        initialSection={initialDetailSection}
        onClose={() => {
          setSelectedTransactionId(null);
          setInitialDetailSection(undefined);
        }}
        formatDateTime={formatDateTime}
      />
    </Container>
  );
};
